/**
 * Sprint 29 M2 — M1: Hold Creation admin (minimal).
 *
 * Lists active Hold Creation rules and opens the capability-aware
 * ProposeIntentSheet for AI-driven creation. Rich condition builder
 * ships in M2; this surface intentionally renders the canonical
 * representation (via `describe`) instead of duplicating it.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Sparkles, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/ui/page-header";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useScope } from "@/hooks/use-scope";

import {
  activateHoldCreationIntent,
  listHoldCreationRules,
  previewHoldCreationRule,
  deleteHoldCreationRule,
} from "@/lib/capabilities/hold-creation/hold-creation.functions";
import { APPROVER_ROLES, type HoldCreationAction } from "@/lib/capabilities/hold-creation/actions";
import { HOLD_REASONS, listTenantCustomers, type HoldReason } from "@/lib/hold-rules.functions";
import { ORDER_FIELDS, describe, type Condition, type Operator } from "@/lib/policy-conditions";
import { ProposeIntentSheet } from "@/components/intents/propose-intent-sheet";
import { trackPolicyEvent } from "@/lib/policy-telemetry";

type RuleListRow = {
  id: string;
  name: string;
  description: string | null;
  scope_level: "tenant" | "customer";
  customer_id: string | null;
  trigger_event: string;
  condition_tree: unknown;
  actions: unknown;
  is_active: boolean;
  priority: number;
  intent_id: string | null;
  intent_prompt: string | null;
  intent_summary: string | null;
  updated_at: string;
  customers: { name: string } | null;
};

export const Route = createFileRoute("/_authenticated/app/admin/hold-creation/advanced")({
  component: HoldCreationAdmin,
  validateSearch: (s) =>
    z.object({ edit: z.string().min(1).optional() }).parse(s),
  errorComponent: ({ error }) => (
    <div className="p-6 text-sm text-destructive">Failed to load: {error.message}</div>
  ),
  notFoundComponent: () => <div className="p-6 text-sm">Not found.</div>,
});

function HoldCreationAdmin() {
  const { tenantId } = useScope();
  useEffect(() => {
    trackPolicyEvent("advanced.opened", {
      tenantId, source: "hold_creation",
      props: { editor: "hold_creation" },
    });
  }, [tenantId]);
  const qc = useQueryClient();
  const listFn = useServerFn(listHoldCreationRules);
  const deleteFn = useServerFn(deleteHoldCreationRule);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editor, setEditor] = useState<
    | { mode: "new" }
    | { mode: "edit"; rule: RuleListRow }
    | { mode: "duplicate"; rule: RuleListRow }
    | null
  >(null);
  const manualOpen = editor !== null;

  const { data, isLoading } = useQuery({
    queryKey: ["hold-creation-rules", tenantId],
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
    enabled: !!tenantId,
  });

  const rows = (data?.rows ?? []) as RuleListRow[];
  const navigate = useNavigate();
  const { edit: editParam } = Route.useSearch();
  useEffect(() => {
    if (!editParam || editor) return;
    const found = rows.find((r) => r.id === editParam);
    if (found) {
      setEditor({ mode: "edit", rule: found });
      navigate({ to: "/app/admin/hold-creation/advanced", search: {}, replace: true });
    }
  }, [editParam, editor, rows, navigate]);

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Policy removed");
      qc.invalidateQueries({ queryKey: ["hold-creation-rules", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Hold Creation Policies — Advanced editor"
        description="Expert editor for Hold Creation policies. The Library is the primary surface; use this only when a policy can't be expressed via Create Policy."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditor({ mode: "new" })} disabled={!tenantId}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              New policy
            </Button>
            <Button size="sm" onClick={() => setSheetOpen(true)} disabled={!tenantId}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Create Policy
            </Button>
          </div>
        }
      />

      {editor && tenantId && (
        <ManualHoldCreationEditor
          tenantId={tenantId}
          mode={editor.mode}
          initial={editor.mode === "new" ? null : editor.rule}
          onClose={() => {
            setEditor(null);
            qc.invalidateQueries({ queryKey: ["hold-creation-rules", tenantId] });
          }}
        />
      )}

      {!manualOpen && isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground p-6">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !manualOpen && rows.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          No hold creation policies yet.
          <div className="mt-2">
            <Button size="sm" variant="outline" onClick={() => setSheetOpen(true)}>
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Describe how you want to place orders on hold
            </Button>
          </div>
        </div>
      ) : !manualOpen ? (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className="rounded-md border p-3 space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">{r.name}</div>
                <div className="flex items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px]">priority {r.priority}</Badge>
                  <Badge variant={r.is_active ? "default" : "secondary"} className="text-[10px]">
                    {r.is_active ? "Active" : "Inactive"}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {r.scope_level === "tenant" ? "Tenant" : (r.customers?.name ?? "Customer")}
                  </Badge>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditor({ mode: "edit", rule: r })}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditor({ mode: "duplicate", rule: r })}>
                    Duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                    onClick={() => {
                      if (confirm(`Remove "${r.name}"?`)) remove.mutate(r.id);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
              {r.description && (
                <p className="text-xs text-muted-foreground">{r.description}</p>
              )}
              <div className="text-[11px]">
                <span className="text-muted-foreground">When </span>
                <code className="text-[10px]">{describe(r.condition_tree as never)}</code>
              </div>
              <div className="text-[11px] flex flex-wrap gap-1">
                <span className="text-muted-foreground mr-1">Do</span>
                {(Array.isArray(r.actions) ? r.actions : []).map((a, i) => {
                  const kind = (a as { kind?: string })?.kind ?? "action";
                  return <Badge key={i} variant="secondary" className="text-[10px]">{kind}</Badge>;
                })}
              </div>
              {r.intent_prompt && (
                <div className="text-[10px] text-muted-foreground border-t pt-1 italic">
                  Proposed from: “{r.intent_prompt}”
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {tenantId && (
        <ProposeIntentSheet
          tenantId={tenantId}
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          initialCapability="hold_creation"
          onActivated={() => qc.invalidateQueries({ queryKey: ["hold-creation-rules", tenantId] })}
        />
      )}
    </div>
  );
}

type PredicateDraft = { field: string; op: Operator; value: string };
type ActionDraft = {
  kind: "require_approval" | "place_hold" | "assign_hold_category";
  reason: HoldReason;
  approverRole: "manager" | "supervisor";
  category: string;
  note: string;
};

function decodeTree(tree: unknown): { combinator: "and" | "or"; predicates: PredicateDraft[] } {
  const toPred = (leaf: { field: string; op: string; value?: unknown }): PredicateDraft => {
    const v = leaf.value;
    const value = Array.isArray(v) ? (v as unknown[]).join(",") : v == null ? "" : String(v);
    return { field: leaf.field, op: leaf.op as Operator, value };
  };
  if (!tree || typeof tree !== "object") {
    return { combinator: "and", predicates: [{ field: "order.subtotal", op: "gt", value: "1000" }] };
  }
  const t = tree as { type: string; field?: string; op?: string; value?: unknown; children?: unknown[] };
  if (t.type === "leaf" && t.field && t.op) return { combinator: "and", predicates: [toPred(t as never)] };
  if ((t.type === "and" || t.type === "or") && Array.isArray(t.children)) {
    const preds = t.children
      .filter((c) => (c as { type?: string }).type === "leaf")
      .map((c) => toPred(c as never));
    return { combinator: t.type, predicates: preds.length > 0 ? preds : [{ field: "order.subtotal", op: "gt", value: "1000" }] };
  }
  return { combinator: "and", predicates: [{ field: "order.subtotal", op: "gt", value: "1000" }] };
}

function decodeAction(actions: unknown): ActionDraft {
  const first = Array.isArray(actions) ? (actions[0] as { kind?: string; params?: Record<string, unknown> } | undefined) : undefined;
  const params = (first?.params ?? {}) as Record<string, unknown>;
  const reason = (params.reason as HoldReason) ?? "manual";
  if (first?.kind === "require_approval") {
    return { kind: "require_approval", reason, approverRole: (params.approver_role as "manager" | "supervisor") ?? "manager", category: "manual-review", note: "" };
  }
  if (first?.kind === "assign_hold_category") {
    return { kind: "assign_hold_category", reason, approverRole: "manager", category: (params.category as string) ?? "manual-review", note: "" };
  }
  return { kind: "place_hold", reason, approverRole: "manager", category: "manual-review", note: (params.note as string) ?? "" };
}

function ManualHoldCreationEditor({
  tenantId,
  mode,
  initial,
  onClose,
}: {
  tenantId: string;
  mode: "new" | "edit" | "duplicate";
  initial: RuleListRow | null;
  onClose: () => void;
}) {
  const previewFn = useServerFn(previewHoldCreationRule);
  const activateFn = useServerFn(activateHoldCreationIntent);
  const customersFn = useServerFn(listTenantCustomers);
  const decoded = useMemo(() => (initial ? decodeTree(initial.condition_tree) : null), [initial]);
  const [name, setName] = useState(
    initial ? (mode === "duplicate" ? `${initial.name} (copy)` : initial.name) : "Manager approval for orders over $1,000",
  );
  const [description, setDescription] = useState(initial?.description ?? "Require manager approval before high-value orders continue fulfillment.");
  const [priority, setPriority] = useState(String(initial?.priority ?? 100));
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  // Duplicate forces the user to pick a different customer.
  const [scopeLevel, setScopeLevel] = useState<"tenant" | "customer">(
    mode === "duplicate" ? "customer" : (initial?.scope_level ?? "tenant"),
  );
  const [customerId, setCustomerId] = useState<string | null>(
    mode === "duplicate" ? null : (initial?.customer_id ?? null),
  );
  const [combinator, setCombinator] = useState<"and" | "or">(decoded?.combinator ?? "and");
  const [predicates, setPredicates] = useState<PredicateDraft[]>(
    decoded?.predicates ?? [{ field: "order.subtotal", op: "gt", value: "1000" }],
  );
  const [action, setAction] = useState<ActionDraft>(
    initial ? decodeAction(initial.actions) : {
      kind: "require_approval",
      reason: "manual",
      approverRole: "manager",
      category: "manual-review",
      note: "",
    },
  );

  const customersQ = useQuery({
    queryKey: ["hold-creation-customers", tenantId],
    queryFn: () => customersFn({ data: { tenantId } }),
    enabled: scopeLevel === "customer",
  });

  const scope = useMemo(() => (
    scopeLevel === "tenant"
      ? { scope_level: "tenant" as const, customer_id: null }
      : customerId
        ? { scope_level: "customer" as const, customer_id: customerId }
        : null
  ), [scopeLevel, customerId]);

  const conditionTree = useMemo<Condition>(() => {
    const leaves: Condition[] = predicates.map((p) => {
      const field = ORDER_FIELDS.find((f) => f.path === p.field);
      const valueless = p.op === "exists" || p.op === "not_exists" || p.op === "is_true" || p.op === "is_false";
      let value: unknown = p.value;
      if (field?.kind === "number") value = Number(p.value) || 0;
      if (field?.kind === "string_array" || p.op === "in" || p.op === "not_in") {
        value = p.value.split(",").map((v) => v.trim()).filter(Boolean);
        if (field?.kind === "string_array" && (p.op === "contains" || p.op === "not_contains")) value = (value as string[])[0] ?? "";
      }
      return valueless
        ? { type: "leaf", field: p.field, op: p.op }
        : { type: "leaf", field: p.field, op: p.op, value };
    });
    return leaves.length === 1 ? leaves[0] : { type: combinator, children: leaves };
  }, [predicates, combinator]);

  const actions = useMemo<HoldCreationAction[]>(() => {
    if (action.kind === "require_approval") {
      return [{ kind: "require_approval", params: { approver_role: action.approverRole, reason: action.reason } }];
    }
    if (action.kind === "assign_hold_category") {
      return [{ kind: "assign_hold_category", params: { category: action.category || "manual-review", reason: action.reason } }];
    }
    return [{ kind: "place_hold", params: { reason: action.reason, ...(action.note ? { note: action.note } : {}) } }];
  }, [action]);

  const preview = useQuery({
    queryKey: ["hold-creation-manual-preview", tenantId, scope, conditionTree],
    queryFn: () => previewFn({ data: { tenantId, scope: scope!, condition_tree: conditionTree, sampleSize: 500 } }),
    enabled: !!scope,
  });

  const save = useMutation({
    mutationFn: () => {
      if (!scope) throw new Error("Pick a customer for customer-scoped policies.");
      const intentId =
        mode === "edit" && initial?.intent_id ? initial.intent_id : crypto.randomUUID();
      const source =
        mode === "edit" && initial?.intent_prompt
          ? "ai_proposal_modified"
          : "manual";
      return activateFn({
        data: {
          tenantId,
          intentId,
          prompt: mode === "edit" && initial?.intent_prompt
            ? initial.intent_prompt
            : `Manual Hold Creation: ${name}`,
          summary: description || name,
          source,
          scope,
          rules: [{
            name,
            description,
            condition_tree: conditionTree,
            actions,
            is_active: isActive,
            priority: Number(priority) || 100,
          }],
        },
      });
    },
    onSuccess: () => {
      toast.success(mode === "edit" ? "Policy updated" : mode === "duplicate" ? "Policy duplicated" : "Policy saved");
      trackPolicyEvent("advanced.saved", {
        source: "hold_creation",
        props: { editor: "hold_creation", mode },
      });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updatePredicate = (index: number, next: PredicateDraft) => {
    const copy = [...predicates];
    copy[index] = next;
    setPredicates(copy);
  };
  const customers = customersQ.data?.rows ?? [];

  return (
    <div className="rounded-md border p-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-medium">
            {mode === "edit" ? "Edit hold creation policy" : mode === "duplicate" ? "Duplicate for another customer" : "New hold creation policy"}
          </h2>
          <p className="text-xs text-muted-foreground">
            {mode === "duplicate"
              ? "Pick the customer this policy should apply to. Conditions and actions are copied from the source policy."
              : "Places holds at order import using canonical conditions and typed actions."}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={isActive} onCheckedChange={setIsActive} />
          <span>{isActive ? "Active" : "Inactive"}</span>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Priority</Label>
          <Input type="number" min={1} max={1000} value={priority} onChange={(e) => setPriority(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Description</Label>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Policy scope</Label>
          <Select value={scopeLevel} onValueChange={(v) => setScopeLevel(v as "tenant" | "customer")}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tenant">Tenant-wide</SelectItem>
              <SelectItem value="customer">Specific customer</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {scopeLevel === "customer" && (
          <div>
            <Label className="text-xs">Customer</Label>
            <Select value={customerId ?? ""} onValueChange={setCustomerId} disabled={customersQ.isLoading}>
              <SelectTrigger><SelectValue placeholder={customersQ.isLoading ? "Loading…" : "Pick a customer"} /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-xs">Conditions</Label>
          <div className="flex items-center gap-2">
            <Select value={combinator} onValueChange={(v) => setCombinator(v as "and" | "or")}>
              <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="and">ALL</SelectItem>
                <SelectItem value="or">ANY</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => setPredicates([...predicates, { field: "order.skus", op: "contains", value: "" }])}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Condition
            </Button>
          </div>
        </div>
        {predicates.map((p, index) => {
          const field = ORDER_FIELDS.find((f) => f.path === p.field) ?? ORDER_FIELDS[0];
          const valueless = p.op === "exists" || p.op === "not_exists" || p.op === "is_true" || p.op === "is_false";
          return (
            <div key={index} className="grid gap-2 sm:grid-cols-[1.4fr_.8fr_1fr_auto]">
              <Select value={p.field} onValueChange={(fieldPath) => {
                const nextField = ORDER_FIELDS.find((f) => f.path === fieldPath) ?? ORDER_FIELDS[0];
                updatePredicate(index, { field: fieldPath, op: nextField.operators[0], value: "" });
              }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ORDER_FIELDS.map((f) => <SelectItem key={f.path} value={f.path}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={p.op} onValueChange={(op) => updatePredicate(index, { ...p, op: op as Operator })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{field.operators.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}</SelectContent>
              </Select>
              {!valueless ? <Input value={p.value} placeholder={field.kind === "string_array" ? "SKU or comma list" : "Value"} onChange={(e) => updatePredicate(index, { ...p, value: e.target.value })} /> : <div />}
              <Button size="sm" variant="ghost" disabled={predicates.length === 1} onClick={() => setPredicates(predicates.filter((_, i) => i !== index))}>Remove</Button>
            </div>
          );
        })}
        <code className="block rounded bg-muted px-2 py-1 text-[11px]">When {describe(conditionTree as never)}</code>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Action</Label>
          <Select value={action.kind} onValueChange={(kind) => setAction({ ...action, kind: kind as ActionDraft["kind"] })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="require_approval">Require approval</SelectItem>
              <SelectItem value="place_hold">Place hold</SelectItem>
              <SelectItem value="assign_hold_category">Assign hold category</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {action.kind === "require_approval" && (
          <div>
            <Label className="text-xs">Approver role</Label>
            <Select value={action.approverRole} onValueChange={(approverRole) => setAction({ ...action, approverRole: approverRole as ActionDraft["approverRole"] })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{APPROVER_ROLES.map((role) => <SelectItem key={role} value={role}>{role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {action.kind === "assign_hold_category" && (
          <div>
            <Label className="text-xs">Category</Label>
            <Input value={action.category} onChange={(e) => setAction({ ...action, category: e.target.value })} />
          </div>
        )}
        <div>
          <Label className="text-xs">Reason</Label>
          <Select value={action.reason} onValueChange={(reason) => setAction({ ...action, reason: reason as HoldReason })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{HOLD_REASONS.map((reason) => <SelectItem key={reason} value={reason}>{reason}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground">
        {preview.isLoading ? "Previewing…" : `${preview.data?.matching_in_sample ?? 0} of ${preview.data?.sample_size ?? 0} sampled orders match this policy.`}
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !name || !scope}>
          {save.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          Save policy
        </Button>
      </div>
    </div>
  );
}